import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "indicator", "card"]
  static values = { step: Number }

  connect() {
    this.cloneItems()
    this.setupInitialPosition()
    this.updateIndicators()
    this.updateCardScales()
    this.containerTarget.addEventListener('scroll', () => this.handleScroll(), { passive: true })
    this.isResetting = false
  }

  cloneItems() {
    const container = this.containerTarget
    const wrapper = container.firstElementChild
    const items = Array.from(wrapper.children).filter(child => !child.dataset.cloned)

    if (items.length === 0) return

    this.originalItemCount = items.length

    // Skip cloning when there is only one item — no infinite scroll needed
    if (items.length <= 1) return

    // Clone items at the end
    items.forEach(item => {
      const cloneEnd = item.cloneNode(true)
      cloneEnd.dataset.cloned = 'end'
      wrapper.appendChild(cloneEnd)
    })

    // Clone items at the beginning
    const reverseItems = [...items].reverse()
    reverseItems.forEach(item => {
      const cloneStart = item.cloneNode(true)
      cloneStart.dataset.cloned = 'start'
      wrapper.insertBefore(cloneStart, wrapper.firstChild)
    })
  }

  setupInitialPosition() {
    const container = this.containerTarget
    const wrapper = container.firstElementChild
    const items = Array.from(wrapper.children).filter(child => !child.dataset.cloned && child.dataset.cloned !== 'start')

    if (items.length === 0) return

    // Single item — no scroll offset needed
    if (this.originalItemCount <= 1) return

    const cardWidth = items[0].offsetWidth
    const gap = parseFloat(window.getComputedStyle(wrapper).gap) || 0
    const step = cardWidth + gap

    // Start at the first original item (after the prepended clones)
    container.scrollLeft = this.originalItemCount * step
  }

  prev() {
    this.scroll(-1)
  }

  next() {
    this.scroll(1)
  }

  scroll(direction) {
    const container = this.containerTarget
    const wrapper = container.firstElementChild
    const allChildren = Array.from(wrapper.children)

    if (allChildren.length === 0) return

    const cardWidth = allChildren[0].offsetWidth
    const gap = parseFloat(window.getComputedStyle(wrapper).gap) || 0
    const step = cardWidth + gap

    container.scrollBy({ left: direction * step, behavior: "smooth" })
  }

  goToSlide(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    const container = this.containerTarget
    const wrapper = container.firstElementChild
    const allChildren = Array.from(wrapper.children)

    if (allChildren.length === 0) return

    const cardWidth = allChildren[0].offsetWidth
    const gap = parseFloat(window.getComputedStyle(wrapper).gap) || 0
    const step = cardWidth + gap

    // Offset by the prepended clones
    const targetPosition = (this.originalItemCount + index) * step
    container.scrollTo({ left: targetPosition, behavior: "smooth" })
  }

  handleScroll() {
    if (this.isResetting) return

    // Debounce the loop check slightly to ensure smooth scrolling finishes
    clearTimeout(this.scrollTimeout)
    this.scrollTimeout = setTimeout(() => {
      this.checkLoopPosition()
    }, 50)

    this.updateIndicators()
    this.updateCardScales()
  }

  checkLoopPosition() {
    if (this.isResetting) return

    const container = this.containerTarget
    const wrapper = container.firstElementChild
    const allChildren = Array.from(wrapper.children)

    if (allChildren.length === 0) return

    const cardWidth = allChildren[0].offsetWidth
    const gap = parseFloat(window.getComputedStyle(wrapper).gap) || 0
    const step = cardWidth + gap
    const totalOriginalWidth = this.originalItemCount * step

    const currentScroll = container.scrollLeft
    const startThreshold = totalOriginalWidth - step // Before reaching the original items
    const endThreshold = (2 * totalOriginalWidth) + step // After passing the original items

    // If scrolled past the cloned items at the end, jump to equivalent position in original set
    if (currentScroll >= endThreshold) {
      this.isResetting = true
      container.scrollLeft = currentScroll - totalOriginalWidth
      setTimeout(() => { this.isResetting = false }, 10)
    }
    // If scrolled before the original items (into start clones), jump to equivalent position at end
    else if (currentScroll <= startThreshold) {
      this.isResetting = true
      container.scrollLeft = currentScroll + totalOriginalWidth
      setTimeout(() => { this.isResetting = false }, 10)
    }
  }

  updateIndicators() {
    if (!this.hasIndicatorTarget) return

    const container = this.containerTarget
    const wrapper = container.firstElementChild
    const allChildren = Array.from(wrapper.children)

    if (allChildren.length === 0) return

    const cardWidth = allChildren[0].offsetWidth
    const gap = parseFloat(window.getComputedStyle(wrapper).gap) || 0
    const step = cardWidth + gap

    // Calculate which original item is currently shown
    // Account for the prepended clones
    const scrollPosition = container.scrollLeft
    const totalIndex = Math.round(scrollPosition / step)
    const currentIndex = (totalIndex - this.originalItemCount) % this.originalItemCount
    const normalizedIndex = currentIndex < 0 ? currentIndex + this.originalItemCount : currentIndex

    this.indicatorTargets.forEach((indicator, i) => {
      const isWhiteScheme = indicator.classList.contains('bg-white') || indicator.classList.contains('bg-white/50')
      const isSlateScheme = indicator.classList.contains('bg-slate-700') || indicator.classList.contains('bg-slate-700/50')
      const isHarmanBlueScheme = indicator.classList.contains('bg-harman-blue') || indicator.classList.contains('bg-harman-blue/10')

      if (i === normalizedIndex) {
        if (isWhiteScheme) {
          indicator.classList.add('bg-white')
          indicator.classList.remove('bg-white/50')
        } else if (isSlateScheme) {
          indicator.classList.add('bg-slate-700')
          indicator.classList.remove('bg-slate-700/50')
        } else if (isHarmanBlueScheme) {
          indicator.classList.add('bg-harman-blue')
          indicator.classList.remove('bg-harman-blue/10')
        }
      } else {
        if (isWhiteScheme) {
          indicator.classList.add('bg-white/50')
          indicator.classList.remove('bg-white')
        } else if (isSlateScheme) {
          indicator.classList.add('bg-slate-700/50')
          indicator.classList.remove('bg-slate-700')
        } else if (isHarmanBlueScheme) {
          indicator.classList.add('bg-harman-blue/10')
          indicator.classList.remove('bg-harman-blue')
        }
      }
    })
  }

  updateCardScales() {
    if (!this.hasCardTarget) return

    const container = this.containerTarget
    const containerCenter = container.offsetLeft + container.offsetWidth / 2

    this.cardTargets.forEach(card => {
      const cardRect = card.getBoundingClientRect()
      const cardCenter = cardRect.left + cardRect.width / 2
      const containerRect = container.getBoundingClientRect()
      const containerCenterAbs = containerRect.left + containerRect.width / 2

      // Calculate distance from center (normalized)
      const distance = Math.abs(cardCenter - containerCenterAbs)
      const maxDistance = containerRect.width / 2
      const normalizedDistance = Math.min(distance / maxDistance, 1)

      // Scale from 0.85 (far) to 1.0 (center)
      const scale = 1 - (normalizedDistance * 0.15)

      card.style.transform = `scale(${scale})`
      card.style.transition = 'transform 0.3s ease-out'
    })
  }
}
